import React, { useEffect, useRef, useState } from "react";

/**
 * Scroll-triggered entrance animation.
 *
 * Deliberately not a motion library: the whole effect is two CSS custom
 * properties and a `data-visible` flag (see `.flr-reveal` in index.css), which
 * keeps it on the compositor and adds nothing to the bundle.
 *
 * Two failure modes this guards against, both of which have bitten this site
 * before with IntersectionObserver-based reveals:
 *
 *  1. Content already in view at mount. Observers do fire for those, but only
 *     after the first frame, and any hydration or layout hiccup leaves the
 *     hero blank. `once: true` plus an immediate `isIntersecting` check on the
 *     first callback covers it — and callers should simply not wrap
 *     above-the-fold content at all.
 *  2. No IntersectionObserver (old browsers, some test environments). We fall
 *     straight through to visible rather than leaving content at opacity 0.
 *
 * `prefers-reduced-motion` is handled in CSS, so it applies even to elements
 * that were already marked visible.
 */
const Reveal = ({
  children,
  delay = 0,
  as: Component = "div",
  image = false,
  className = "",
  style,
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;

    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      // Fire slightly before the element reaches the viewport so the motion
      // has finished by the time it's properly in view.
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Component
      ref={ref}
      data-visible={visible ? "true" : "false"}
      className={`flr-reveal${image ? " flr-reveal-image" : ""}${className ? ` ${className}` : ""}`}
      style={{ "--flr-reveal-delay": `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </Component>
  );
};

export default Reveal;
